import type {
  AdminBulkUserStatusResult,
  AdminChannel,
  AdminChannelDetail,
  AdminChannelListQuery,
  AdminListQuery,
  AdminLoginInput,
  AdminMe,
  AdminPage,
  AdminSession,
  AdminStats,
  AdminUpdateChannelInput,
  AdminUpdateUserInput,
  AdminUser,
  ClearHistoryResponse,
  ConfigExport,
  ConfigImportResult,
  LdapSettingsResponse,
  LdapSyncResult,
  LdapTestInput,
  LdapTestResult,
  RevokeSessionsResponse,
  SettingsResponse,
  UpdateLdapConfigInput,
  UpdateSettingsInput,
  UserSession,
} from '@orbit-sync/shared-types';
import { API_V1_PREFIX, apiErrorSchema } from '@orbit-sync/shared-types';
import { getAdminToken } from '../adminTokenStorage.js';
import { API_BASE_URL, request } from './client.js';
import { ApiRequestError, NetworkError } from './errors.js';

/**
 * The administrator API, `/api/v1/admin/*`.
 *
 * Every call except `login` carries the ADMIN session token (`as: 'admin'`),
 * never a user's access token. All shapes come from shared-types.
 */

const asAdmin = { as: 'admin' } as const;

type ListQuery = Partial<AdminListQuery>;

export function login(input: AdminLoginInput): Promise<AdminSession> {
  return request<AdminSession>('/admin/auth/login', {
    method: 'POST',
    body: input,
    authenticated: false,
  });
}

export function logout(): Promise<void> {
  return request<void>('/admin/auth/logout', { ...asAdmin, method: 'POST' });
}

export function me(signal?: AbortSignal): Promise<AdminMe> {
  return request<AdminMe>('/admin/auth/me', { ...asAdmin, signal });
}

export function stats(signal?: AbortSignal): Promise<AdminStats> {
  return request<AdminStats>('/admin/stats', { ...asAdmin, signal });
}

// ---- Users -------------------------------------------------------------------

export function listUsers(query: ListQuery, signal?: AbortSignal): Promise<AdminPage<AdminUser>> {
  return request<AdminPage<AdminUser>>('/admin/users', {
    ...asAdmin,
    query: { q: query.q || undefined, limit: query.limit, offset: query.offset },
    signal,
  });
}

export function updateUser(userId: string, input: AdminUpdateUserInput): Promise<AdminUser> {
  return request<AdminUser>(`/admin/users/${userId}`, { ...asAdmin, method: 'PATCH', body: input });
}

export function setUserDisabled(userId: string, disabled: boolean): Promise<AdminUser> {
  return request<AdminUser>(`/admin/users/${userId}/status`, {
    ...asAdmin,
    method: 'PUT',
    body: { disabled },
  });
}

/** `PUT /admin/users/status` - enables or disables up to `ADMIN_BULK_USERS_MAX` accounts. */
export function setUsersDisabled(
  userIds: string[],
  disabled: boolean,
): Promise<AdminBulkUserStatusResult> {
  return request<AdminBulkUserStatusResult>('/admin/users/status', {
    ...asAdmin,
    method: 'PUT',
    body: { userIds, disabled },
  });
}

export function unlockUser(userId: string): Promise<AdminUser> {
  return request<AdminUser>(`/admin/users/${userId}/unlock`, { ...asAdmin, method: 'POST' });
}

export function listUserSessions(userId: string, signal?: AbortSignal): Promise<UserSession[]> {
  return request<UserSession[]>(`/admin/users/${userId}/sessions`, { ...asAdmin, signal });
}

export function endUserSessions(userId: string): Promise<RevokeSessionsResponse> {
  return request<RevokeSessionsResponse>(`/admin/users/${userId}/sessions`, {
    ...asAdmin,
    method: 'DELETE',
  });
}

export function endSession(sessionId: string): Promise<RevokeSessionsResponse> {
  return request<RevokeSessionsResponse>(`/admin/sessions/${sessionId}`, {
    ...asAdmin,
    method: 'DELETE',
  });
}

/** Signs every user out everywhere. */
export function endAllSessions(): Promise<RevokeSessionsResponse> {
  return request<RevokeSessionsResponse>('/admin/sessions', { ...asAdmin, method: 'DELETE' });
}

export function deleteUser(userId: string): Promise<void> {
  return request<void>(`/admin/users/${userId}`, { ...asAdmin, method: 'DELETE' });
}

// ---- Channels ----------------------------------------------------------------

export function listChannels(
  query: Partial<AdminChannelListQuery>,
  signal?: AbortSignal,
): Promise<AdminPage<AdminChannel>> {
  return request<AdminPage<AdminChannel>>('/admin/channels', {
    ...asAdmin,
    query: {
      q: query.q || undefined,
      type: query.type,
      limit: query.limit,
      offset: query.offset,
    },
    signal,
  });
}

export function getChannel(channelId: string, signal?: AbortSignal): Promise<AdminChannelDetail> {
  return request<AdminChannelDetail>(`/admin/channels/${channelId}`, { ...asAdmin, signal });
}

export function updateChannel(
  channelId: string,
  input: AdminUpdateChannelInput,
): Promise<AdminChannelDetail> {
  return request<AdminChannelDetail>(`/admin/channels/${channelId}`, {
    ...asAdmin,
    method: 'PATCH',
    body: input,
  });
}

export function deleteChannel(channelId: string): Promise<void> {
  return request<void>(`/admin/channels/${channelId}`, { ...asAdmin, method: 'DELETE' });
}

export function clearChannelHistory(channelId: string): Promise<ClearHistoryResponse> {
  return request<ClearHistoryResponse>(`/admin/channels/${channelId}/messages`, {
    ...asAdmin,
    method: 'DELETE',
  });
}

export function clearAllHistory(): Promise<ClearHistoryResponse> {
  return request<ClearHistoryResponse>('/admin/messages', { ...asAdmin, method: 'DELETE' });
}

// ---- Settings ------------------------------------------------------------------

export function getSettings(signal?: AbortSignal): Promise<SettingsResponse> {
  return request<SettingsResponse>('/admin/settings', { ...asAdmin, signal });
}

export function updateSettings(changes: UpdateSettingsInput): Promise<SettingsResponse> {
  return request<SettingsResponse>('/admin/settings', {
    ...asAdmin,
    method: 'PATCH',
    body: changes,
  });
}

// ---- Directory (LDAP / Active Directory) ---------------------------------------

export function getLdap(signal?: AbortSignal): Promise<LdapSettingsResponse> {
  return request<LdapSettingsResponse>('/admin/ldap', { ...asAdmin, signal });
}

export function updateLdap(input: UpdateLdapConfigInput): Promise<LdapSettingsResponse> {
  return request<LdapSettingsResponse>('/admin/ldap', { ...asAdmin, method: 'PUT', body: input });
}

export function testLdap(input: LdapTestInput): Promise<LdapTestResult> {
  return request<LdapTestResult>('/admin/ldap/test', { ...asAdmin, method: 'POST', body: input });
}

export function syncLdap(): Promise<LdapSyncResult> {
  return request<LdapSyncResult>('/admin/ldap/sync', { ...asAdmin, method: 'POST' });
}

// ---- Configuration and backup -------------------------------------------------

/** `GET /admin/config/export` - settings and directory configuration (no secrets). */
export function exportConfig(): Promise<ConfigExport> {
  return request<ConfigExport>('/admin/config/export', asAdmin);
}

/** `POST /admin/config/import` - applies a file from `exportConfig`. */
export function importConfig(file: unknown): Promise<ConfigImportResult> {
  return request<ConfigImportResult>('/admin/config/import', {
    ...asAdmin,
    method: 'POST',
    body: file,
  });
}

/**
 * `GET /admin/backup` - a `pg_dump` of the database, saved by the browser.
 * Fetched rather than linked because the route needs the bearer token.
 */
export async function downloadBackup(): Promise<void> {
  const token = getAdminToken();
  let response: Response;

  try {
    response = await fetch(`${API_BASE_URL}${API_V1_PREFIX}/admin/backup`, {
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
  } catch (error) {
    throw new NetworkError(error);
  }

  if (!response.ok) {
    const body = apiErrorSchema.safeParse(await response.json().catch(() => null));
    throw new ApiRequestError(
      response.status,
      body.success
        ? body.data
        : { error: { code: 'internal_error', message: `Backup failed (${response.status})` } },
    );
  }

  const name =
    /filename="([^"]+)"/.exec(response.headers.get('content-disposition') ?? '')?.[1] ??
    'orbit-sync.dump';
  saveBlob(await response.blob(), name);
}

/** Hands a blob to the browser as a download. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}
